import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — communication group.
 * Lighting model and construction rules live in {@link OtIcon}.
 */

type TProps = SVGProps<SVGSVGElement>;

const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/* ------------------------------------------------------------------ Forum */
/** Two bubbles: a conversation with more than one voice. */
export const OtForum = (props: TProps) => (
	<OtIcon
		slug='forum'
		palette='green'
		groundX={46}
		groundY={82}
		groundR={22}
		extraDefs={(refs, id) => (
			<linearGradient id={id('teal')} gradientUnits='userSpaceOnUse' x1='56' y1='34' x2='84' y2='72'>
				<stop offset='0' stopColor='#9ce9d4' />
				<stop offset='.5' stopColor='#46bcaa' />
				<stop offset='1' stopColor='#12433a' />
			</linearGradient>
		)}
		{...props}>
		{(r) => (
			<>
				<path
					d='M62 34h11a9 9 0 0 1 9 9v14a9 9 0 0 1-9 9h-4v9l-11-9h-3z'
					fill={r.own('teal')}
				/>
				<path
					d='M62 34h11a9 9 0 0 1 9 9v14a9 9 0 0 1-9 9h-4v9l-11-9h-3z'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
					strokeLinejoin='round'
				/>
				<ellipse
					cx='64'
					cy='47'
					rx='7'
					ry='14'
					fill={r.tone.occlusion}
					opacity='.38'
					filter={r.tight}
				/>
				<path
					d='M23 15h30a11 11 0 0 1 11 11v17a11 11 0 0 1-11 11H36L22 66V53a11 11 0 0 1-10-11V26a11 11 0 0 1 11-11z'
					fill={r.fill}
				/>
				<path
					d='M23 15h30a11 11 0 0 1 11 11v17a11 11 0 0 1-11 11H36L22 66V53a11 11 0 0 1-10-11V26a11 11 0 0 1 11-11z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<circle cx='27' cy='35' r='4' fill='#fff' fillOpacity='.92' />
				<circle cx='39' cy='35' r='4' fill='#e2dcfa' fillOpacity='.85' />
				<circle cx='51' cy='35' r='4' fill='#b4a8e8' fillOpacity='.8' />
				<Spec d='M19 25a9 9 0 0 1 9-7.6h13' o={0.9} w={2.5} />
				<Bounce d='M77 58a8 8 0 0 1-6 5.4' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Chat */
/** One bubble: a direct thread. */
export const OtChat = (props: TProps) => (
	<OtIcon slug='chat' palette='green' groundX={46} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				<path
					d='M20 14h52a12 12 0 0 1 12 12v26a12 12 0 0 1-12 12H40L22 76V64a12 12 0 0 1-10-12V26a12 12 0 0 1 12-12z'
					fill={r.fill}
				/>
				<path
					d='M20 14h52a12 12 0 0 1 12 12v26a12 12 0 0 1-12 12H40L22 76V64a12 12 0 0 1-10-12V26a12 12 0 0 1 12-12z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				<rect x='25' y='29' width='44' height='5.6' rx='2.8' fill='#fff' fillOpacity='.9' />
				<rect x='25' y='40' width='32' height='5.6' rx='2.8' fill='#fff' fillOpacity='.62' />
				<Spec d='M20 25a10 10 0 0 1 10-8h14' o={0.9} w={2.5} />
				<Bounce d='M79 50a11 11 0 0 1-8 8' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Email */
export const OtEmail = (props: TProps) => (
	<OtIcon slug='email' palette='blue' groundX={48} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				<rect x='10' y='22' width='76' height='50' rx='12' fill={r.fill} />
				<rect
					x='10'
					y='22'
					width='76'
					height='50'
					rx='12'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
				/>
				{/* the flap: its underside is in shadow, which is what makes the envelope read as open */}
				<path d='M12 28l36 26 36-26' fill='none' stroke={r.tone.occlusion} strokeOpacity='.5' strokeWidth='7' strokeLinejoin='round' />
				<path d='M11 27l37 26 37-26' fill='none' stroke={r.body} strokeWidth='4.4' strokeLinejoin='round' />
				<Spec d='M17 32a10 10 0 0 1 9-7h14' w={2.4} />
				<Bounce d='M80 60a10 10 0 0 1-7 7' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Inbox */
export const OtInbox = (props: TProps) => (
	<OtIcon slug='inbox' palette='indigo' groundX={48} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				<rect x='10' y='20' width='76' height='52' rx='12' fill={r.fill} />
				<rect
					x='10'
					y='20'
					width='76'
					height='52'
					rx='12'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
				/>
				{/* the tray mouth, cut out of the front face */}
				<path
					d='M10 48h20a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6h20'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='6'
					strokeLinejoin='round'
				/>
				<path
					d='M10 47h20a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6h20'
					fill='none'
					stroke={r.body}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				<rect x='30' y='30' width='36' height='5' rx='2.5' fill='#fff' fillOpacity='.55' />
				<Spec d='M17 30a10 10 0 0 1 9-7h14' w={2.4} />
				<Bounce d='M80 60a10 10 0 0 1-7 7' c={r.tone.bounce} o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Send */
export const OtSend = (props: TProps) => (
	<OtIcon slug='send' palette='cyan' groundX={46} groundY={80} groundR={20} {...props}>
		{(r) => (
			<>
				{/* far wing, in shadow */}
				<path d='M84 14L12 42l30 8z' fill={r.fill} opacity='.6' />
				{/* near wing, lit */}
				<path d='M84 14L42 50l4 26z' fill={r.fill} />
				<path
					d='M84 14L12 42l30 8 4 26z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				{/* the crease between the two wings */}
				<path d='M84 14L42 50' stroke={r.tone.occlusion} strokeOpacity='.55' strokeWidth='2.4' />
				<Spec d='M22 40L74 20' o={0.55} w={2.2} />
				<Bounce d='M46 74l3-18' c='#7fe6d2' o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Share */
export const OtShare = (props: TProps) => (
	<OtIcon slug='share' palette='violet' groundX={46} groundY={82} groundR={22} {...props}>
		{(r) => (
			<>
				<path
					d='M32 42L66 24M32 48L66 66'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='7'
					strokeLinecap='round'
				/>
				<path
					d='M31 41L65 23M31 47L65 65'
					stroke={r.rim}
					strokeWidth='4.4'
					strokeLinecap='round'
				/>
				<circle cx='22' cy='45' r='15' fill={r.fill} />
				<circle cx='22' cy='45' r='15' fill='none' stroke={r.rim} strokeWidth='3' />
				<circle cx='72' cy='20' r='13' fill={r.fill} />
				<circle cx='72' cy='20' r='13' fill='none' stroke={r.rim} strokeWidth='2.8' />
				<circle cx='72' cy='68' r='13' fill={r.fill} />
				<circle cx='72' cy='68' r='13' fill='none' stroke={r.rim} strokeWidth='2.8' />
				<Spec d='M14 38a14 14 0 0 1 7.5-4.4' />
				<Bounce d='M81 62a13 13 0 0 1-6 8' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Notifications */
export const OtNotifications = (props: TProps) => (
	<OtIcon
		slug='notifications'
		palette='gold'
		groundX={48}
		groundY={79}
		groundR={20}
		extraDefs={(refs, id) => (
			<linearGradient id={id('bone')} gradientUnits='userSpaceOnUse' x1='30' y1='14' x2='62' y2='72'>
				<stop offset='0' stopColor='#ffffff' />
				<stop offset='.6' stopColor='#f2e8d3' />
				<stop offset='1' stopColor='#a8977a' />
			</linearGradient>
		)}
		{...props}>
		{(r) => (
			<>
				<path d='M46 14a5 5 0 0 1 5 5v3h-10v-3a5 5 0 0 1 5-5z' fill={r.own('bone')} />
				<path
					d='M46 20c-11 0-19 9-19 21v10l-6 8v3h50v-3l-6-8V41c0-12-8-21-19-21z'
					fill={r.fill}
				/>
				<path
					d='M46 20c-11 0-19 9-19 21v10l-6 8v3h50v-3l-6-8V41c0-12-8-21-19-21z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				{/* inner wall: the dome is hollow, so the far side of the mouth is lit from inside */}
				<path
					d='M46 24c-8.6 0-15 7-15 17v10.6'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.34'
					strokeWidth='1.6'
				/>
				<path d='M38 65h16a8 8 0 0 1-16 0z' fill={r.own('bone')} />
				<Spec d='M33 41a14 14 0 0 1 7.5-11.6' o={0.95} />
				<Bounce d='M62 46v9' c='#ffd9a8' o={0.45} />
				{/* No unread dot here on purpose: `NotificationBell` positions a real badge carrying
				    the count over this button, and a painted one would sit beside it. */}
			</>
		)}
	</OtIcon>
);
